import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, RotateCcw, Timer, Trophy, CheckCircle2, Eraser } from "lucide-react";
import GameBoard from "@/components/GameBoard";
import LetterBank from "@/components/LetterBank";
import GameOverDialog from "@/components/GameOverDialog";
import { DIFFICULTY_CONFIG, computeFinalScore, formatTime, shuffle } from "@/lib/scoring";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stable per-browser anonymous id for daily-challenge ranking (no account needed).
function getAnonId() {
  try {
    let id = localStorage.getItem("fraseology_anon_id");
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID()) ||
        `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("fraseology_anon_id", id);
    }
    return id;
  } catch {
    return `anon-${Math.random().toString(36).slice(2, 12)}`;
  }
}

export default function Game() {
  const { difficulty } = useParams();
  const navigate = useNavigate();
  const isDaily = difficulty === "daily";
  const [dailyMeta, setDailyMeta] = useState(null); // {date, difficulty, plays_today}
  const cfg = isDaily
    ? DIFFICULTY_CONFIG[dailyMeta?.difficulty || "normal"]
    : DIFFICULTY_CONFIG[difficulty] || DIFFICULTY_CONFIG.easy;

  const [phrase, setPhrase] = useState(""); // target phrase, spaces preserved
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]); // array of {char, locked, errored} for each letter position (non-space)
  const [positions, setPositions] = useState([]); // array of {type: "letter"|"space", slotIndex?}
  const [bank, setBank] = useState([]); // array of {id, char, used}
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeSlot, setActiveSlot] = useState(null); // selected target slot for next placement
  const timerRef = useRef(null);

  // Load phrase
  const loadPhrase = useCallback(async () => {
    setLoading(true);
    setFinished(false);
    setFinalResult(null);
    setSeconds(0);
    try {
      let p, meta = null;
      if (isDaily) {
        const res = await axios.get(`${API}/daily`);
        p = res.data.phrase;
        meta = res.data;
        setDailyMeta(meta);
      } else {
        const res = await axios.get(`${API}/phrase`, { params: { difficulty } });
        p = res.data.phrase;
      }
      setPhrase(p);

      // Build positions and slots
      const pos = [];
      const sl = [];
      let slotIdx = 0;
      for (const ch of p) {
        if (ch === " ") {
          pos.push({ type: "space" });
        } else {
          pos.push({ type: "letter", slotIndex: slotIdx });
          sl.push({ char: "", locked: false, errored: false, target: ch });
          slotIdx++;
        }
      }
      setPositions(pos);
      setSlots(sl);
      setActiveSlot(sl.length > 0 ? 0 : null);

      // Build shuffled bank
      const letters = p.replace(/ /g, "").split("");
      const shuffled = shuffle(letters);
      setBank(
        shuffled.map((c, i) => ({
          id: `${i}-${c}-${Math.random().toString(36).slice(2, 6)}`,
          char: c,
          used: false,
        }))
      );
      setRunning(true);
    } catch (err) {
      toast.error("Could not load a phrase. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [difficulty, isDaily]);

  useEffect(() => {
    loadPhrase();
  }, [loadPhrase]);

  // Timer
  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  // Derived: running score (live, simple — unlocked letters are 0; based only on locked count)
  const lockedCount = slots.filter((s) => s.locked).length;
  const liveScore = useMemo(() => {
    const base = cfg.base;
    const letters = lockedCount * cfg.perLetter;
    const timePenalty = seconds * 5;
    return Math.max(0, base + letters - timePenalty);
  }, [cfg, lockedCount, seconds]);

  // Place a letter from the bank into the selected slot (activeSlot) if set,
  // otherwise into the next empty non-locked slot (left-to-right fallback).
  const placeLetter = (bankId) => {
    if (finished) return;
    const bankItem = bank.find((b) => b.id === bankId);
    if (!bankItem || bankItem.used) return;

    let targetIdx = -1;
    if (
      activeSlot !== null &&
      slots[activeSlot] &&
      !slots[activeSlot].locked &&
      slots[activeSlot].char === ""
    ) {
      targetIdx = activeSlot;
    } else {
      targetIdx = slots.findIndex((s) => !s.locked && s.char === "");
    }
    if (targetIdx === -1) return;

    const placedIdx = targetIdx;
    setSlots((prev) =>
      prev.map((s, i) =>
        i === placedIdx ? { ...s, char: bankItem.char, bankId: bankId, errored: false } : s
      )
    );
    setBank((prev) =>
      prev.map((b) => (b.id === bankId ? { ...b, used: true } : b))
    );

    // Advance active slot to next empty slot after placedIdx (wrap to start if none after)
    setActiveSlot(() => {
      const afterIdx = slots.findIndex(
        (s, i) => i > placedIdx && !s.locked && s.char === ""
      );
      if (afterIdx !== -1) return afterIdx;
      const fromStart = slots.findIndex(
        (s, i) => i < placedIdx && !s.locked && s.char === ""
      );
      return fromStart !== -1 ? fromStart : null;
    });
  };

  // Click a board slot:
  // - if it has a letter and isn't locked -> return to bank (unplace)
  // - if it is empty and isn't locked -> set it as the active target for next placement
  const handleSlotClick = (slotIdx) => {
    if (finished) return;
    const slot = slots[slotIdx];
    if (!slot || slot.locked) return;
    if (slot.char) {
      unplaceLetter(slotIdx);
      setActiveSlot(slotIdx);
      return;
    }
    setActiveSlot((prev) => (prev === slotIdx ? null : slotIdx));
  };

  // Remove a placed (non-locked) letter — send back to bank
  const unplaceLetter = (slotIdx) => {
    if (finished) return;
    const slot = slots[slotIdx];
    if (!slot || slot.locked || !slot.char) return;
    const bankId = slot.bankId;

    setSlots((prev) =>
      prev.map((s, i) =>
        i === slotIdx ? { ...s, char: "", bankId: undefined, errored: false } : s
      )
    );
    if (bankId) {
      setBank((prev) =>
        prev.map((b) => (b.id === bankId ? { ...b, used: false } : b))
      );
    }
  };

  const clearUnlocked = () => {
    if (finished) return;
    const returning = [];
    setSlots((prev) =>
      prev.map((s) => {
        if (!s.locked && s.char) {
          if (s.bankId) returning.push(s.bankId);
          return { ...s, char: "", bankId: undefined, errored: false };
        }
        return s;
      })
    );
    setBank((prev) =>
      prev.map((b) => (returning.includes(b.id) ? { ...b, used: false } : b))
    );
    setActiveSlot(null);
  };

  // Submit -> validate
  const handleSubmit = async () => {
    if (finished || submitting) return;
    // need all slots filled (non-locked) to submit
    const allFilled = slots.every((s) => s.char !== "");
    if (!allFilled) {
      toast("Place all letters first.", { description: "Every box must have a letter before submitting." });
      return;
    }
    setSubmitting(true);

    // Determine correct vs wrong
    const returningIds = [];
    const newSlots = slots.map((s) => {
      if (s.locked) return s;
      if (s.char === s.target) {
        return { ...s, locked: true, errored: false, bankId: undefined };
      }
      // wrong: mark errored, will clear after animation
      if (s.bankId) returningIds.push(s.bankId);
      return { ...s, errored: true };
    });
    setSlots(newSlots);

    // After shake animation, return wrong ones to bank
    setTimeout(() => {
      setSlots((prev) =>
        prev.map((s) =>
          s.errored
            ? { ...s, char: "", errored: false, bankId: undefined }
            : s
        )
      );
      setBank((prev) =>
        prev.map((b) => (returningIds.includes(b.id) ? { ...b, used: false } : b))
      );
      setSubmitting(false);
      setActiveSlot(null);

      // check if solved
      const solved = newSlots.every((s) => s.locked || s.char === s.target);
      if (solved) {
        finishGame(newSlots.length);
      }
    }, 750);
  };

  const finishGame = async (correctLetters) => {
    setRunning(false);
    clearInterval(timerRef.current);
    const effectiveDifficulty = isDaily ? (dailyMeta?.difficulty || "normal") : difficulty;
    const result = computeFinalScore({
      difficulty: effectiveDifficulty,
      correctLetters,
      seconds,
    });

    if (isDaily) {
      // Persist daily score and fetch rank/percentile
      try {
        const anonId = getAnonId();
        const { data } = await axios.post(`${API}/daily/score`, {
          score: result.total,
          time_seconds: seconds,
          under_minute: result.underMinute,
          anon_id: anonId,
        });
        result.daily = data;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[daily] failed to submit:", err);
      }
      setFinalResult(result);
      setFinished(true);
      return;
    }

    setFinalResult(result);
    setFinished(true);

    try {
      await axios.post(`${API}/score`, {
        difficulty,
        phrase,
        score: result.total,
        time_seconds: seconds,
        under_minute: result.underMinute,
      });
    } catch (err) {
      // Score persistence is best-effort; log for observability but don't break the UX.
      // eslint-disable-next-line no-console
      console.warn("[game] failed to persist score:", err);
    }
  };

  return (
    <div className="relative min-h-screen w-full pb-56 md:pb-64" data-testid="game-page">
      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-12 pt-6 md:pt-10">
        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6 md:mb-10 pb-4 border-b-2 border-[#1A1A1A]">
          <button
            onClick={() => navigate("/")}
            data-testid="back-home-btn"
            className="flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.2em] font-semibold hover:text-[#EB5E28] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </button>
          <div className="flex items-center gap-3">
            <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#6B6A68]">
              {isDaily ? "Edition" : "Volume"}
            </span>
            <span className="font-serif-display text-xl font-semibold" data-testid="game-difficulty-label">
              {isDaily ? `${dailyMeta?.date || "Today"} · ${cfg.label}` : cfg.label}
            </span>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-12">
          <div className="bg-white border-2 border-[#1A1A1A] px-3 py-3 md:px-5 md:py-4 shadow-[3px_3px_0px_#1A1A1A]">
            <div className="flex items-center gap-2 mb-1">
              <Timer className="w-3.5 h-3.5 text-[#6B6A68]" />
              <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-semibold">
                Time
              </span>
            </div>
            <div
              className="font-serif-display text-2xl md:text-3xl font-semibold tabular-nums"
              data-testid="game-timer"
            >
              {formatTime(seconds)}
            </div>
          </div>
          <div className="bg-white border-2 border-[#1A1A1A] px-3 py-3 md:px-5 md:py-4 shadow-[3px_3px_0px_#1A1A1A]">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-3.5 h-3.5 text-[#6B6A68]" />
              <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-semibold">
                Score
              </span>
            </div>
            <div
              className="font-serif-display text-2xl md:text-3xl font-semibold tabular-nums"
              data-testid="game-score"
            >
              {liveScore}
            </div>
          </div>
          <div className="bg-[#1A1A1A] text-[#F4F1EA] border-2 border-[#1A1A1A] px-3 py-3 md:px-5 md:py-4 shadow-[3px_3px_0px_#EB5E28]">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#EB5E28]" />
              <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-[#EB5E28] font-semibold">
                Locked
              </span>
            </div>
            <div
              className="font-serif-display text-2xl md:text-3xl font-semibold tabular-nums"
              data-testid="game-locked-count"
            >
              {lockedCount}/{slots.length}
            </div>
          </div>
        </div>

        {/* Board */}
        {loading ? (
          <div className="text-center py-20 font-serif-display italic text-2xl text-[#6B6A68]">
            Setting the type…
          </div>
        ) : (
          <>
            <GameBoard
              positions={positions}
              slots={slots}
              activeSlot={activeSlot}
              onSlotClick={handleSlotClick}
            />

            {/* Action buttons */}
            <div className="flex items-center justify-center gap-3 md:gap-4 mt-10 md:mt-14 flex-wrap">
              <button
                onClick={clearUnlocked}
                data-testid="clear-btn"
                disabled={finished}
                className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] font-sans-ui font-semibold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Eraser className="w-4 h-4" />
                Clear
              </button>
              <button
                onClick={handleSubmit}
                data-testid="submit-guess-btn"
                disabled={finished || submitting}
                className="inline-flex items-center gap-2 px-7 py-3 bg-[#EB5E28] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-sm shadow-[4px_4px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[6px_6px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle2 className="w-4 h-4" />
                Submit
              </button>
              <button
                onClick={loadPhrase}
                data-testid="new-phrase-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] font-sans-ui font-semibold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                New phrase
              </button>
            </div>
          </>
        )}
      </div>

      {/* Letter bank — fixed bottom */}
      {!loading && (
        <LetterBank bank={bank} onTileClick={placeLetter} disabled={finished} />
      )}

      <AnimatePresence>
        {finished && finalResult && (
          <GameOverDialog
            result={finalResult}
            phrase={phrase}
            seconds={seconds}
            difficulty={isDaily ? (dailyMeta?.difficulty || "normal") : difficulty}
            isDaily={isDaily}
            dailyMeta={dailyMeta}
            onPlayAgain={loadPhrase}
            onHome={() => navigate("/")}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
